import { useEffect, useRef, useState } from 'react'
import {
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  computeJobEndFromProcessing,
  type JobProcessingUnit,
} from '@/lib/job-schedule-form'

export type JobScheduleFormFields = {
  start_date?: string
  start_time?: string
  end_date?: string
  end_time?: string
  processing_time?: string
  processing_unit?: JobProcessingUnit
}

export type JobScheduleFieldsProps = {
  control: Control<JobScheduleFormFields>
  register: UseFormRegister<JobScheduleFormFields>
  setValue: UseFormSetValue<JobScheduleFormFields>
  errors?: FieldErrors<JobScheduleFormFields>
}

export function bindJobScheduleFormFields<T extends JobScheduleFormFields>(form: {
  control: Control<T>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  errors?: FieldErrors<T>
}): JobScheduleFieldsProps {
  return {
    control: form.control as unknown as Control<JobScheduleFormFields>,
    register: form.register as unknown as UseFormRegister<JobScheduleFormFields>,
    setValue: form.setValue as unknown as UseFormSetValue<JobScheduleFormFields>,
    errors: form.errors as FieldErrors<JobScheduleFormFields> | undefined,
  }
}

export function JobScheduleFields({
  control,
  register,
  setValue,
  errors,
}: JobScheduleFieldsProps) {
  const [endOverridden, setEndOverridden] = useState(false)
  const endOverriddenRef = useRef(false)

  const startDate = useWatch({ control, name: 'start_date' })
  const startTime = useWatch({ control, name: 'start_time' })
  const endDate = useWatch({ control, name: 'end_date' })
  const endTime = useWatch({ control, name: 'end_time' })
  const processingTime = useWatch({ control, name: 'processing_time' })
  const processingUnit = useWatch({ control, name: 'processing_unit' })

  useEffect(() => {
    endOverriddenRef.current = endOverridden
  }, [endOverridden])

  useEffect(() => {
    if (endOverriddenRef.current) {
      return
    }

    const computedEnd = computeJobEndFromProcessing(
      startDate,
      startTime,
      processingTime,
      processingUnit ?? 'hr',
    )

    if (!computedEnd) {
      return
    }

    if (computedEnd.date !== (endDate ?? '') || computedEnd.time !== (endTime ?? '')) {
      setValue('end_date', computedEnd.date, { shouldDirty: true })
      setValue('end_time', computedEnd.time, { shouldDirty: true })
    }
  }, [startDate, startTime, processingTime, processingUnit, endDate, endTime, setValue])

  const resetEndToCalculated = () => {
    const computedEnd = computeJobEndFromProcessing(
      startDate,
      startTime,
      processingTime,
      processingUnit ?? 'hr',
    )

    if (computedEnd) {
      setValue('end_date', computedEnd.date, { shouldDirty: true })
      setValue('end_time', computedEnd.time, { shouldDirty: true })
    }

    setEndOverridden(false)
    endOverriddenRef.current = false
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start_date">Start date *</Label>
          <Input
            id="start_date"
            type="date"
            {...register('start_date')}
          />
          {errors?.start_date ? (
            <p className="text-sm text-destructive">{errors.start_date.message}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="start_time">Start time *</Label>
          <Input
            id="start_time"
            type="time"
            step={60}
            {...register('start_time')}
          />
          {errors?.start_time ? (
            <p className="text-sm text-destructive">{errors.start_time.message}</p>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="processing_time">Processing time</Label>
        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-2">
          <Input
            id="processing_time"
            type="number"
            min="0"
            step="any"
            placeholder="Ex: 2"
            {...register('processing_time')}
          />
          <Select
            value={processingUnit ?? 'hr'}
            onValueChange={(value: JobProcessingUnit) => {
              setValue('processing_unit', value, { shouldDirty: true })
            }}
          >
            <SelectTrigger id="processing_unit">
              <SelectValue placeholder="Unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="min">Minutes</SelectItem>
              <SelectItem value="hr">Hours</SelectItem>
              <SelectItem value="day">Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">
          End date and time are calculated from start + processing time. You can override them below.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="end_date">End date</Label>
            {endOverridden ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={resetEndToCalculated}
              >
                <RotateCcw className="h-3 w-3" />
                Use calculated
              </Button>
            ) : null}
          </div>
          <Input
            id="end_date"
            type="date"
            {...register('end_date', {
              onChange: () => setEndOverridden(true),
            })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="end_time">End time</Label>
          <Input
            id="end_time"
            type="time"
            step={60}
            {...register('end_time', {
              onChange: () => setEndOverridden(true),
            })}
          />
        </div>
      </div>
    </div>
  )
}
